import type { FlowInstanceStatus, FlowProcessState } from "@repo/shared/types";
import { Check, LayoutTemplate, Lock, Pencil, RotateCcw, Send, UserPlus, X } from "lucide-react";
import { useState } from "react";
import { FlowStageBadge } from "@/components/flow/flow-stage-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { flowStageHelper } from "./flow-stage-helper";
import { ProcessButton } from "./process-button";
import { T } from "./wizard-header.i18n";

type WizardHeaderProps = {
  instanceId: string;
  instanceName: string | null;
  flowName: string;
  flowDescription?: string | null;
  status: string;
  clientId: string;
  isCompleted: boolean;
  /** Compliance reviewer without advisor permission: hide all mutating actions. */
  isReadOnly?: boolean;
  isSaving: boolean;
  isCompleting: boolean;
  hasSuitabilityBlockers: boolean;
  hasWithdrawalExceedsValue: boolean;
  needsSnapshotRegeneration: boolean;
  /** QL-675 — the worker is rebuilding the snapshots; there is nothing to click. */
  snapshotsRegenerating: boolean;
  /** QL-239 — the flow is in preview and this user is not a designer (server-derived). */
  submitBlockedByPreview: boolean;
  canInvite: boolean;
  /**
   * QL-399 — stage, stop and reopen-eligibility. Null while the process state
   * is still loading or could not be read: the header then renders without the
   * stage helper and the counter rather than claiming there is nothing to see.
   */
  processState: FlowProcessState | null;
  onSaveName: (name: string) => Promise<void>;
  onComplete: () => void;
  onCancelAdvice: () => void;
  onReopen: () => void;
  onInviteClick: () => void;
  /** If true, shows minimal header without description */
  minimal?: boolean;
};

export function WizardHeader({
  instanceId,
  instanceName,
  flowName,
  flowDescription,
  status,
  clientId,
  isCompleted,
  isReadOnly = false,
  isSaving,
  isCompleting,
  hasSuitabilityBlockers,
  hasWithdrawalExceedsValue,
  needsSnapshotRegeneration,
  snapshotsRegenerating,
  submitBlockedByPreview,
  canInvite,
  processState,
  onSaveName,
  onComplete,
  onCancelAdvice,
  onReopen,
  onInviteClick,
  minimal = false,
}: WizardHeaderProps) {
  const t = useT(T);
  // The stage badge and helper line take the language explicitly.
  const { userLanguage: language } = useUserLanguage();

  // QL-399 — every one of these degrades to "nothing to show" when the process
  // state is unavailable, so a failed read never invents a calm, empty header.
  const stopped = processState?.stopped ?? null;
  const messages = processState?.messages ?? [];
  const reopen = processState?.reopen ?? null;
  // Offered when it can actually be done, plus the one blocked case worth
  // explaining: a signed document. "editable" has nothing to reopen and
  // "completed" is final — for both the action is absent, not disabled, so an
  // advisor is never shown a dead control with no reason next to it.
  const reopenBlockedNote = reopen?.blockedReason === "signed" ? t.blockedSigned : null;
  const showReopen = !isReadOnly && !!reopen && (reopen.available || !!reopenBlockedNote);
  const stageHelper = processState
    ? flowStageHelper(
        processState.status as FlowInstanceStatus,
        stopped,
        !!reopen?.available && !isReadOnly,
        language,
      )
    : null;

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(instanceName || "");
  const [isSavingName, setIsSavingName] = useState(false);

  const handleSaveName = async () => {
    setIsSavingName(true);
    try {
      await onSaveName(editedName);
      setIsEditingName(false);
    } catch (err) {
      log.error({ err }, "failed to save flow instance name");
      // Keep editing mode open so user can retry
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(instanceName || "");
    setIsEditingName(false);
  };

  const displayName = instanceName || flowName;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Name, Template */}
        <div className="flex items-center gap-3">
          {isEditingName ? (
            <div>
              <div className="flex items-center gap-2">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder={flowName}
                  maxLength={25}
                  className="text-2xl font-bold h-auto py-1 px-2 w-64"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                />
                <Button
                  size="icon"
                  tooltip={t.confirm}
                  variant="ghost"
                  onClick={handleSaveName}
                  disabled={isSavingName}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  tooltip={t.cancelEdit}
                  variant="ghost"
                  onClick={handleCancelEdit}
                >
                  <X className="h-4 w-4" />
                </Button>
                <FlowStageBadge status={status} language={language} />
              </div>
              {instanceName && (
                <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5 italic">
                  <LayoutTemplate className="h-3.5 w-3.5" />
                  {flowName}
                </p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold">{displayName}</h1>
                {!isReadOnly &&
                  !isCompleted &&
                  (status === "draft" || status === "in_progress") && (
                    <Button
                      size="icon"
                      tooltip={t.edit}
                      variant="ghost"
                      onClick={() => setIsEditingName(true)}
                      className="ml-1"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                <FlowStageBadge status={status} language={language} />
              </div>
              {instanceName && (
                <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5 italic">
                  <LayoutTemplate className="h-3.5 w-3.5" />
                  {flowName}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: Actions. QL-399 — Process is always last, and `flex-wrap`
            keeps that true when the row wraps: Cancel → Submit → Process while
            editable, Reopen flow → Process while locked. */}
        <div className="flex flex-wrap items-center justify-end gap-3 shrink-0">
          {isSaving && <span className="text-sm text-muted-foreground">{t.saving}</span>}

          {canInvite && (
            <Button variant="outline" onClick={onInviteClick} className="gap-2">
              <UserPlus className="h-4 w-4" />
              {t.inviteClient}
            </Button>
          )}

          {!isCompleted && !isReadOnly && (
            <Button
              variant="outline"
              onClick={onCancelAdvice}
              disabled={isSaving || isCompleting}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              {t.cancelAdvice}
            </Button>
          )}

          {/* Shown even when blocked, with the reason under the header: an
              advisor should never be left guessing why it is not on offer. */}
          {showReopen && (
            <Button
              variant="outline"
              onClick={onReopen}
              disabled={!reopen?.available}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              {t.reopen}
            </Button>
          )}

          {!isCompleted &&
            !isReadOnly &&
            (hasSuitabilityBlockers ||
            hasWithdrawalExceedsValue ||
            needsSnapshotRegeneration ||
            snapshotsRegenerating ||
            submitBlockedByPreview ? (
              // Button's tooltip gives the disabled button a focusable wrapper,
              // so the reason is reachable from the keyboard too.
              <Button
                disabled
                className="gap-2"
                tooltip={
                  <>
                    {hasSuitabilityBlockers && <p>{t.cannotSubmitUnsuitableProducts}</p>}
                    {hasWithdrawalExceedsValue && <p>{t.cannotSubmitWithdrawalsExceed}</p>}
                    {snapshotsRegenerating ? (
                      <p>{t.cannotSubmitSnapshotsRegenerating}</p>
                    ) : (
                      needsSnapshotRegeneration && <p>{t.cannotSubmitSnapshotsStale}</p>
                    )}
                    {submitBlockedByPreview && <p>{t.cannotSubmitPreview}</p>}
                  </>
                }
              >
                <Send className="h-4 w-4" />
                {t.submit}
              </Button>
            ) : (
              <Button
                onClick={onComplete}
                disabled={isSaving}
                loading={isCompleting}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {t.submit}
              </Button>
            ))}

          {/* Process stays available at every stage — after reopening and once
              every message is dismissed — and is the only entry to process
              details and history. */}
          <ProcessButton
            instanceId={instanceId}
            clientId={clientId}
            messageCount={messages.length}
            stopped={!!stopped}
          />
        </div>
      </div>

      {/* A blocked reopen is the more useful thing to say, so it takes the
          helper's place rather than sitting next to a contradictory one. */}
      {(reopenBlockedNote ?? stageHelper) && (
        <p className="text-muted-foreground">{reopenBlockedNote ?? stageHelper}</p>
      )}
      {reopenBlockedNote && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {t.blockedSignedHint}
        </p>
      )}
      {!minimal && flowDescription && <p className="text-muted-foreground">{flowDescription}</p>}
    </div>
  );
}
